import * as React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import s from './DarkMenu.module.scss';

export type DarkMenuOptions = Array<{
  label?: string;
  optionContent?: React.ReactNode;
  onClick: () => void;
  value?: string | number;
}>;

type Props = {
  label?: string;
  title?: string;
  options: DarkMenuOptions;
  disableAutoCloseAfterClick?: boolean;
  selectedValue?: string | number;
  getOptionValue?: (option: DarkMenuOptions[0], index: number) => string | number | undefined;
  description?: string | React.ReactNode;
};

const DEFAULT_SLOT_PROPS = {
  // @ts-ignore
  list: { 'aria-labelledby': 'basic-button' },
  paper: {
    sx: {
      marginTop: '8px',
    },
  },
};

export function DarkMenu({
  label,
  title = 'menu',
  options = [],
  disableAutoCloseAfterClick = false,
  selectedValue,
  getOptionValue,
  description,
}: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  if (options.length === 0) return null;

  const open = Boolean(anchorEl);
  const handleClose = () => setAnchorEl(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);

  const handledOptions = options.map((option, idx) => {
    const key = option.label || `option-${idx}`;
    const content = option.optionContent || option.label || 'Option';

    // Determine if this option is selected
    let isSelected = false;
    if (selectedValue !== undefined) {
      if (getOptionValue) {
        const optionValue = getOptionValue(option, idx);
        isSelected = optionValue !== undefined && optionValue === selectedValue;
      } else if (option.value !== undefined) {
        isSelected = option.value === selectedValue;
      }
    }

    return (
      <MenuItem
        key={key}
        selected={isSelected}
        onClick={() => {
          option?.onClick();
          if (!disableAutoCloseAfterClick) {
            handleClose();
          }
        }}
      >
        {content}
      </MenuItem>
    );
  });

  return (
    <div>
      <button
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        className={s.buttonTitle}
      >
        {label ? <span className={s.buttonLabel}>{label}</span> : null}
        {title}
      </button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          ...DEFAULT_SLOT_PROPS,
          paper: {
            ...DEFAULT_SLOT_PROPS.paper,
            className: s.darkMenu,
          },
        }}
      >
        {handledOptions}
        {description && <div className={s.descriptionSection}>{description}</div>}
      </Menu>
    </div>
  );
}
